import { useEffect, useId, useRef, useState } from "react";
import FormFieldBuilder from "../FormBuilder/FormFieldBuilder";
import { Button, Input, Select } from "../../../ui";
import { Checkbox } from "../../../ui/Checkbox";
import "./CategoryModal.css";
import { useModalLayer } from "../../../modals/modalHooks";

// Deep copy so edits (including nested form fields) never touch the loaded
// category list until the save succeeds.
const cloneCategory = (category) => ({
  code: category.code || "",
  name: { ar: "", en: "", ...(category.name || {}) },
  description: { ar: "", en: "", ...(category.description || {}) },
  active: category.active ?? true,
  formFields: (category.formFields || []).map((field) => ({
    ...field,
    label: { ...(field.label || {}) },
    options: {
      ar: [...(field.options?.ar || [])],
      en: [...(field.options?.en || [])],
    },
  })),
});

export default function CategoryModal({
  category,
  categoryCodes,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState({
    code: "",
    name: {
      ar: "",
      en: "",
    },
    description: {
      ar: "",
      en: "",
    },
    active: true,
    formFields: [],
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!category) return;
    setForm(cloneCategory(category));
  }, [category]);

  const requestClose = () => {
    if (!saving) onClose();
  };

  useModalLayer(true, { containerRef: dialogRef, onEscape: requestClose });

  const submit = async () => {
    if (saving) return;
    if (!form.code) {
      setError("Select a category code.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSubmit(form);
    } catch (err) {
      console.error(err);
      // Keep the dialog open with everything entered.
      setError(err?.message || "The category could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="category-modal-overlay" role="presentation">
      <div
        ref={dialogRef}
        className="category-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={saving || undefined}
        tabIndex={-1}
      >
        <div className="category-modal-header">
          <h2 className="category-modal-title" id={titleId}>
            {category ? "Edit Category" : "Create Category"}
          </h2>
        </div>

        <div className="category-modal-grid">
          <div className="category-field full-width">
            <Select
              label="Category Code"
              value={form.code}
              placeholder="Select Category"
              disabled={!!category}
              options={categoryCodes.map((code) => ({ value: code, label: code }))}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  code: e.target.value,
                }))
              }
            />
          </div>

          <div className="category-field">
            <Input
              label="Arabic Name"
              placeholder="Enter Arabic name"
              value={form.name.ar}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  name: {
                    ...prev.name,
                    ar: e.target.value,
                  },
                }))
              }
            />
          </div>

          <div className="category-field">
            <Input
              label="English Name"
              placeholder="Enter English name"
              value={form.name.en}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  name: {
                    ...prev.name,
                    en: e.target.value,
                  },
                }))
              }
            />
          </div>

          <div className="category-field">
            <Input
              textarea
              label="Arabic Description"
              placeholder="Enter Arabic description"
              value={form.description.ar}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  description: {
                    ...prev.description,
                    ar: e.target.value,
                  },
                }))
              }
            />
          </div>

          <div className="category-field">
            <Input
              textarea
              label="English Description"
              placeholder="Enter English description"
              value={form.description.en}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  description: {
                    ...prev.description,
                    en: e.target.value,
                  },
                }))
              }
            />
          </div>

          <div className="category-field full-width">
            <Checkbox
              label="Active"
              checked={form.active}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  active: e.target.checked,
                }))
              }
            />
          </div>
        </div>

        <div className="category-section">
          <FormFieldBuilder
            fields={form.formFields}
            setFields={(fields) =>
              setForm((prev) => ({
                ...prev,
                formFields: fields,
              }))
            }
          />
        </div>

        {error && (
          <p className="support-action-error" role="alert">
            {error}
          </p>
        )}

        <div className="category-modal-actions">
          <Button
            variant="danger"
            appearance="soft"
            onClick={requestClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            appearance="filled"
            onClick={submit}
            disabled={saving}
            loading={saving}
          >
            Save Category
          </Button>
        </div>
      </div>
    </div>
  );
}
